import ColumnDefinition from '../ColumnDefinition.js';
import ContextMenuDefinition from '../ContextMenuDefinition.js';
import { getCurrencySymbol } from 'utils/currency';
import { apply, parse } from '@sellsy/numberformat';
import { CompassEnums } from '@sellsy/compass2';
import { OcrEvent } from 'types/events';
import {
    OcrDocumentState,
    NON_VERIFIABLE_OCR_STATES,
    type OcrListingRow,
    type OcrLinkedType,
} from 'types/entities/ocr/ocr.type';

interface TypeConfig {
    label: string;
    color: string;
}

const TYPE_CONFIG: Record<OcrLinkedType, TypeConfig> = {
    purInvoice: { label: 'Facture', color: CompassEnums.PillColor.TEAL },
    purCreditNote: { label: 'Avoir', color: CompassEnums.PillColor.PURPLE },
};

function formatAmount(totalExclTax: string | null, row: OcrListingRow): string {
    if (totalExclTax === null) {
        return '';
    }

    const { numformat } = (
        window.sellsyapp.config.globalProperties.$store.state as {
            corp: { numformat: Record<string, unknown> };
        }
    ).corp;
    const currencySymbol = (getCurrencySymbol(row.currency ?? '') as string) || row.currency || '';

    return apply(parse(totalExclTax), {
        ...numformat,
        currency: currencySymbol,
        precision: 2,
    }).trim();
}

export default {
    defaultSort: { field: 'created_at', direction: 'desc' },
    columns: [
        new ColumnDefinition(
            'file_name',
            window.i18n.__('Document'),
            false,
            false,
            (fileName: string, row: OcrListingRow) => {
                if (NON_VERIFIABLE_OCR_STATES.includes(row.state)) {
                    return fileName;
                }

                return {
                    type: 'component',
                    name: 'cell-link',
                    params: {
                        label: fileName,
                        event: OcrEvent.VERIFY,
                        eventParams: { id: row.id },
                        dataBot: 'purchases-purinvoices-list_verify-invoice_click',
                    },
                };
            },
            { width: 'XXL' },
        ),
        new ColumnDefinition(
            'created_at',
            window.i18n.__("Date d'entrée"),
            false,
            true,
            (date: string) =>
                window.sellsyapp.config.globalProperties.$filters.dateFormatter(
                    date,
                    'SLASHED_DATE_TIME',
                ),
            { width: 'M' },
        ),
        new ColumnDefinition(
            'linked_type',
            window.i18n.__('Type'),
            false,
            true,
            (linkedType: string) => {
                const config = TYPE_CONFIG[linkedType as OcrLinkedType];
                if (!config) {
                    return linkedType;
                }

                return {
                    type: 'component',
                    name: 'cell-wrapper',
                    params: {
                        componentName: 'compass-pill',
                        componentProps: {
                            variant: CompassEnums.PillVariant.PASTEL,
                            size: CompassEnums.PillSize.SMALL,
                            label: window.i18n.__(config.label),
                            color: config.color,
                        },
                    },
                };
            },
            { width: 'S' },
        ),
        new ColumnDefinition(
            'source',
            window.i18n.__('Source'),
            false,
            true,
            (source: string, row: OcrListingRow) => {
                if (source === 'einvoicing') {
                    return window.i18n.__('Électronique');
                }
                if (row.linked_type === 'purInvoice') {
                    return window.i18n.__('Importée');
                }
                return window.i18n.__('Importé');
            },
            { width: 'M' },
        ),
        new ColumnDefinition(
            'state',
            window.i18n.__('Statut'),
            false,
            true,
            (state: string, row: OcrListingRow) => ({
                type: 'component',
                name: 'ocr-item-state',
                params: {
                    state,
                    id: row.id,
                    errorCode: row.error_code,
                    isElectronicDoc: row.source === 'einvoicing',
                },
            }),
            { width: 'M' },
        ),
        new ColumnDefinition(
            'supplier',
            window.i18n.__('Fournisseur'),
            false,
            true,
            (supplier: string | null) => supplier || '',
            { width: 'XL' },
        ),
        new ColumnDefinition(
            'total_excl_tax',
            window.i18n.__('Montant HT'),
            false,
            true,
            (totalExclTax: string | null, row: OcrListingRow) => formatAmount(totalExclTax, row),
            { width: 'M', alignment: 'right' },
        ),
    ],
    filters: [],
    hooks: {
        metas: (metas: Record<string, unknown>): Record<string, unknown> => metas,
        fetch: (data: { filters: { state: string[] } }): void => {
            data.filters.state = Object.values(OcrDocumentState);
        },
    },
    contextMenus: [
        new ContextMenuDefinition(
            'verify',
            window.i18n.__('Vérifier'),
            (row: OcrListingRow) => ({ event: OcrEvent.VERIFY, params: { id: row.id } }),
            'vuejs',
            '_parent',
            function (
                this: { metas: { user_can_validate_pur_invoice: boolean } },
                row: OcrListingRow,
            ): boolean {
                return (
                    !this.metas.user_can_validate_pur_invoice ||
                    NON_VERIFIABLE_OCR_STATES.includes(row.state)
                );
            },
            'purchases-purinvoices-list_verify-invoice_row',
        ),
        new ContextMenuDefinition(
            'delete',
            window.i18n.__('Supprimer'),
            (row: OcrListingRow) => ({ event: OcrEvent.DELETE, params: { id: row.id } }),
            'vuejs',
            '_parent',
            function (
                this: { metas: { user_can_validate_pur_invoice: boolean } },
                row: OcrListingRow,
            ): boolean {
                return row.source === 'einvoicing' || !this.metas.user_can_validate_pur_invoice;
            },
            'purchases-purinvoices-list_delete-invoice_row',
            (row: OcrListingRow): string =>
                row.source === 'einvoicing'
                    ? window.i18n.__('ocr.OcrList.electronic_delete_tooltip')
                    : '',
        ),
    ],
    batchActions: [],
};
